// Dispatches a single MessagePart to its renderer.
// Text parts render through react-markdown so bold/italic/lists/links from
// the LLM come through cleanly. Tables render via InlineTable. Charts
// reuse ChartDispatcher from the Packer Posture chart generator — the
// consultant's chart tool embeds the full chart payload into the message
// part, so the dispatcher renders without needing a fresh fetch.

import { Link as LinkIcon, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { MessagePart } from "@/types/consultant";
import { InlineTable } from "./InlineTable";
import { ShareableChart } from "./ShareableChart";
import {
  chartHeadline, chartCardSeries, formatHeadlineValue, formatHeadlineDelta,
} from "@/lib/chartHeadline";
import type { Provenance } from "@/types/consultantProvenance";
import { ProvenanceRow } from "./ProvenanceChip";
import { ChartDispatcher } from "@/components/packer-chart/ChartDispatcher";
import type { PackerChartSeriesPayload } from "@/hooks/usePackerChartSeries";
import { useConsultant } from "@/contexts/ConsultantContext";

// Clickable follow-up suggestions. Clicking a chip auto-sends the prompt
// via sendMessage, same as if the user typed it. Disabled while another
// message is pending so rapid-click doesn't queue multiple requests.
function SuggestionChips({
  items,
  surface,
}: {
  items: string[];
  surface: "dark" | "light";
}) {
  const { sendMessage, isPending } = useConsultant();
  if (!items?.length) return null;
  const isLight = surface === "light";
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {items.map((item, i) => (
        <button
          key={i}
          onClick={() => sendMessage(item)}
          disabled={isPending}
          className={
            isLight
              ? "inline-flex items-center gap-1 rounded-full border border-[#8F7032]/40 bg-[#8F7032]/8 hover:bg-[#8F7032]/15 px-2.5 py-1 text-xs text-[#1A1A1A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              : "inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/5 hover:bg-primary/10 px-2.5 py-1 text-xs text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          }
        >
          <Sparkles
            className={
              isLight ? "h-3 w-3 text-[#8F7032]" : "h-3 w-3 text-primary"
            }
          />
          {item}
        </button>
      ))}
    </div>
  );
}

export function MessagePartRenderer({
  part,
  surface = "dark",
  messageId,
}: {
  part: MessagePart;
  // "dark" = prose-invert (light text on dark surface — default for MI
  // drawer and Clara user bubbles). "light" = standard prose (dark
  // text on the ivory data surface used by Clara assistant bubbles
  // where charts and tables render).
  surface?: "dark" | "light";
  // Phase 5 — passed to ProvenanceRow so chip clicks can open the
  // Show Data drawer with the full tool-call chain for this message.
  messageId?: string | null;
}) {
  const isLight = surface === "light";
  switch (part.type) {
    case "text":
      // Phase 5 — text parts can carry provenance for tools that emit
      // attribution-only parts (get_cash_feeder_summary, get_sale_barn_detail).
      // If the content is empty (the prose lives in the LLM-synthesized
      // sibling text part), render only the provenance chip row.
      if (!part.content) {
        return <ProvenanceRow provenance={part.provenance} surface={surface} messageId={messageId} />;
      }
      return (
        <div>
          <div
            className={
              isLight
                ? "text-sm leading-relaxed text-[#1A1A1A] break-words prose prose-sm max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0 prose-strong:text-[#1A1A1A] prose-strong:font-semibold prose-a:text-[#8F7032] prose-a:underline prose-code:text-[#1A1A1A] prose-code:bg-black/5 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none prose-th:text-[#1A1A1A] prose-th:font-semibold prose-td:text-[#1A1A1A]"
                : "text-sm leading-relaxed text-foreground break-words prose prose-sm prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0 prose-strong:text-foreground prose-strong:font-semibold prose-a:text-primary prose-a:underline prose-code:text-foreground prose-code:bg-muted/40 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em] prose-code:before:content-none prose-code:after:content-none prose-th:text-foreground prose-th:font-semibold prose-td:text-foreground"
            }
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {part.content}
            </ReactMarkdown>
          </div>
          <ProvenanceRow provenance={part.provenance} surface={surface} messageId={messageId} />
        </div>
      );

    case "table":
      return (
        <div>
          <InlineTable part={part} surface={surface} />
          <ProvenanceRow provenance={part.provenance} surface={surface} messageId={messageId} />
        </div>
      );

    case "chart": {
      // The chart MessagePart carries the full payload shape expected by
      // ChartDispatcher (template_id, series, meta, chart_type, etc.) —
      // embedded by the consultant's render_boxed_beef_chart tool. Cast
      // through unknown because our MessagePart.chart type is structurally
      // identical to PackerChartSeriesPayload but declared separately.
      const payload = part as unknown as PackerChartSeriesPayload;
      // Derived from the same series the chart draws, and null whenever "the
      // latest value and its change" is ambiguous. See chartHeadline.
      const head = chartHeadline(payload.series as never, {
        chartType: payload.chart_type,
        xAxis: payload.x_axis,
      });
      // The whole chart, redrawn at card size when its shape allows. Null for
      // anything the card would have to guess at — those are photographed.
      const card = chartCardSeries(payload.series as never, {
        chartType: payload.chart_type,
        xAxis: payload.x_axis,
      });
      return (
        <ShareableChart
          title={part.title ?? "Chart"}
          source={citationLine(part.provenance)}
          headline={head ? formatHeadlineValue(head.value, payload.y_axis_format) : undefined}
          headlineDelta={
            head?.delta != null ? formatHeadlineDelta(head.delta, payload.y_axis_format) : undefined
          }
          headlineDir={
            head?.delta == null ? undefined : head.delta > 0 ? "up" : head.delta < 0 ? "down" : "flat"
          }
          series={card?.series}
          bars={card?.bars}
          formatValue={
            payload.y_axis_format === "integer"
              ? (v: number) => Math.round(v).toLocaleString()
              : undefined
          }
        >
          <ChartDispatcher payload={payload} isLoading={false} error={null} />
          <ProvenanceRow provenance={part.provenance} surface={surface} messageId={messageId} />
        </ShareableChart>
      );
    }

    case "suggestions": {
      return <SuggestionChips items={part.items} surface={surface} />;
    }

    case "link": {
      const isExternal = /^https?:\/\//i.test(part.href);
      const linkClass = isLight
        ? "inline-flex items-center gap-1.5 rounded-md border border-[#E5E5DF] bg-black/5 px-2.5 py-1 text-xs text-[#1A1A1A] hover:bg-black/10 my-1"
        : "inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/20 px-2.5 py-1 text-xs text-foreground hover:bg-accent my-1";
      if (isExternal) {
        return (
          <a
            href={part.href}
            target="_blank"
            rel="noreferrer"
            className={linkClass}
          >
            <LinkIcon className="h-3 w-3" />
            {part.label}
          </a>
        );
      }
      return (
        <Link to={part.href} className={linkClass}>
          <LinkIcon className="h-3 w-3" />
          {part.label}
        </Link>
      );
    }
  }
}


/**
 * One line of citation for a shared chart picture.
 *
 * ⚠️ A CHART LEAVING THE APP LOSES ITS PROVENANCE CHIP. The chip lives in the
 * DOM beside the chart, not inside the SVG, so a rasterised picture carries none
 * of it — and a price line texted to a lender with no source is worse than the
 * text case, because a line without a label cannot even be argued with. This
 * flattens the same provenance into a caption the picture can carry.
 */
function citationLine(prov: Provenance | Provenance[] | undefined): string | undefined {
  if (!prov) return undefined;
  const list = Array.isArray(prov) ? prov : [prov];
  const parts = list
    .map((p) => [p.report_name, p.as_of_date].filter(Boolean).join(" · "))
    .filter(Boolean);
  const seen = Array.from(new Set(parts));
  return seen.length ? seen.join("  |  ") : undefined;
}
